import { useEffect, useRef } from 'react'

import { ChevronIcon } from '../copSiteDetails/component/SiteDetailsIcons'
import { FilterType } from './hooks/useAssetsTree'

type AssetsTreeHeaderProps = {
  selectedFilter: FilterType
  isDropdownOpen: boolean
  allAssetsLabel?: string
  spareAssetsLabel?: string
  decommissionedAssetsLabel?: string
  expandAllLabel?: string
  collapseAllLabel?: string
  onToggleDropdown: () => void
  onFilterSelect: (filter: FilterType) => void
  onExpandAll: () => void
  onCollapseAll: () => void
  onClose: () => void
}

export default function AssetsTreeHeader({
  selectedFilter, isDropdownOpen,
  allAssetsLabel, spareAssetsLabel, decommissionedAssetsLabel,
  expandAllLabel, collapseAllLabel,
  onToggleDropdown, onFilterSelect, onExpandAll, onCollapseAll, onClose,
}: Readonly<AssetsTreeHeaderProps>) {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isDropdownOpen) return undefined
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onToggleDropdown()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDropdownOpen, onToggleDropdown])

  const filterLabelMap: Record<FilterType, string | undefined> = {
    all: allAssetsLabel, spare: spareAssetsLabel, decom: decommissionedAssetsLabel,
  }
  const filterOption = (filter: FilterType, label?: string) => (
    <button
      type="button"
      className={`assets-tree-popup__filter-option ${selectedFilter === filter ? 'selected' : ''}`}
      onClick={() => onFilterSelect(filter)}
    >
      {label} {selectedFilter === filter && <span>(Selected)</span>}
    </button>
  )

  return (
    <div className="assets-tree-popup__custom-header">
      <div className="assets-tree-popup__header-left">
        <div className="assets-tree-popup__filter-dropdown" ref={dropdownRef}>
          <button
            type="button"
            className="assets-tree-popup__filter-button"
            onClick={(e) => { e.stopPropagation(); onToggleDropdown() }}
          >
            {filterLabelMap[selectedFilter]}
            <span className="assets-tree-popup__filter-arrow"><ChevronIcon open={isDropdownOpen} /></span>
          </button>
          {isDropdownOpen && (
            <div className="assets-tree-popup__filter-menu">
              {filterOption('all', allAssetsLabel)}
              {filterOption('spare', spareAssetsLabel)}
              {filterOption('decom', decommissionedAssetsLabel)}
            </div>
          )}
        </div>
        {selectedFilter === 'all' && (
          <div className="assets-tree-popup__actions">
            <button type="button" className="assets-tree-popup__action-btn" onClick={onExpandAll}>{expandAllLabel}</button>
            <button type="button" className="assets-tree-popup__action-btn" onClick={onCollapseAll}>{collapseAllLabel}</button>
          </div>
        )}
      </div>
      <button type="button" className="assets-tree-popup__close-button" aria-label="Close" onClick={onClose}>&#x2715;</button>
    </div>
  )
}
